import { useState, type ReactNode } from "react";
import { Copy, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Anzeige-Label für das Token-Feld (z.B. "Agent-Token", "Channel-Token"). */
  label?: string;
  token: string;
}

// Generischer One-Time-Display für Plain-Tokens. Master gibt Tokens nur einmal
// bei Create zurück — UI muss sie sofort sichtbar machen. hideCloseButton:
// kein X-Schließen, User klickt expliziten Button.
export function OneTimeTokenDialog({
  open,
  onOpenChange,
  title,
  description,
  label = "Token",
  token,
}: Props) {
  const [copied, setCopied] = useState(false);

  async function copyToClipboard() {
    // clipboard.writeText kann rejecten (kein secure context, Permission denied).
    // One-Time-Secret darf nicht still verloren gehen → Fehler sichtbar machen,
    // Wert bleibt im <code> selektierbar.
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren fehlgeschlagen — Wert manuell markieren und kopieren.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => o && onOpenChange(o)}>
      <DialogContent hideCloseButton data-testid="token-display-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md bg-yellow/10 border border-yellow/30 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-yellow mt-0.5 shrink-0" />
          <div className="text-yellow">
            Diese Token-Anzeige ist einmalig. Nach dem Schließen kann der Token nicht mehr abgerufen werden.
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-subtext0">{label}</label>
          <div className="flex gap-2">
            <code
              className="flex-1 rounded-md border border-border bg-base px-3 py-2 font-mono text-xs text-text break-all"
              data-testid="token-value"
            >
              {token}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              data-testid="token-copy"
              aria-label="Token kopieren"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            data-testid="token-close"
          >
            Token kopiert, Dialog schliessen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
